/**
 * The chat-first editor's decisions, kept out of React.
 *
 * Everything here is pure so it can be pinned by tests without a renderer:
 * how an EditResult lands in the cached review, what the rail says about each
 * chapter, what the empty chat suggests, how a failed request is worded, and
 * what context a chat message carries.
 *
 * Type-only imports from the hooks, so loading this module never drags the
 * REST client (and its '@'-aliased interceptors) into a test.
 */
import type {
  ReviewChapter,
  ReviewPayload,
} from '../../../Hooks/LmsCourseApi/useCourseReview';
import {
  chapterStatus,
  nextToReview,
  ReviewFlag,
  ReviewPage,
} from '../reviewLogic';
import {STATUS} from '../courseTheme';
import {initialPuzzleRun, puzzleBoardView, PuzzleRun} from '../courseLogic';
import type {CourseMove} from '../../../Hooks/LmsCourseApi/types';

// ---------------------------------------------------------------------------
// API shapes (spec §2). Declared here, beside the code that consumes them, so
// the hook and the screen share one definition.

export type EditBatch = {id: string; labels: string[]; undone: boolean};

export type EditResult = {
  /** Every chapter touched or created, in full (same shape as the review GET). */
  chapters: ReviewChapter[];
  /** Chapters deleted — the undo of an add_puzzle. */
  removed_chapter_ids: number[];
  /** chapter id -> chapter_number for the whole book. JSON keys arrive as strings. */
  numbering: Record<string | number, number>;
  batch: EditBatch;
  review_state: string;
  summary: ReviewPayload['summary'];
};

export type ChatMessage = {
  id: number | string;
  role: 'user' | 'assistant';
  text: string;
  batch_id: string | null;
  labels: string[];
  created_at: string;
  /** Client-only: a message on its way to the server. */
  pending?: boolean;
};

export type Revision = {
  batch_id: string;
  source: 'editor' | 'chat' | string;
  labels: string[];
  created_by: number | null;
  created_at: string;
  undone_at: string | null;
};

// ---------------------------------------------------------------------------
// Merging a result into the cached review

/**
 * Fold an EditResult into the review payload: touched chapters replaced,
 * created ones inserted, removed ones dropped, the whole book renumbered.
 *
 * Untouched chapters keep their object identity, so only what changed
 * re-renders — the board of an unrelated chapter is never remounted.
 */
export function mergeEditResult(
  payload: ReviewPayload,
  result: EditResult,
): ReviewPayload {
  const removed = new Set(result.removed_chapter_ids ?? []);
  const touched = new Map((result.chapters ?? []).map(c => [c.id, c]));
  const numbering = result.numbering ?? {};
  const numberOf = (c: ReviewChapter) => {
    const n = numbering[c.id] ?? numbering[String(c.id)];
    return typeof n === 'number' ? n : c.number;
  };

  const kept: ReviewChapter[] = [];
  for (const c of payload.chapters) {
    if (removed.has(c.id)) continue;
    const next = touched.get(c.id) ?? c;
    touched.delete(c.id);
    const number = numberOf(next);
    kept.push(number === next.number ? next : {...next, number});
  }
  for (const created of touched.values()) {
    if (removed.has(created.id)) continue;
    const number = numberOf(created);
    kept.push(number === created.number ? created : {...created, number});
  }
  // Stable: equal numbers keep their order.
  const chapters = kept
    .map((c, i) => ({c, i}))
    .sort((a, b) => a.c.number - b.c.number || a.i - b.i)
    .map(x => x.c);

  const review_state = (result.review_state ??
    payload.review_state) as ReviewPayload['review_state'];
  return {
    ...payload,
    chapters,
    review_state,
    // Any edit to a published course sends it back to review and off the shelf.
    book:
      review_state === 'published'
        ? payload.book
        : {...payload.book, is_public: false},
    summary: result.summary ?? payload.summary,
  };
}

// ---------------------------------------------------------------------------
// Status, progress and the rail

export type StatusView = {
  level: 'bad' | 'warn' | 'good' | 'fixed' | 'dismissed' | 'unknown';
  word: string;
  icon: string;
  fg: string;
  bg: string;
  needsReview: boolean;
};

const isPuzzle = (p: ReviewPage) => p.content_type === 'chess_position';

/** One chapter's status as icon + word + colour — never colour alone. */
export function statusView(
  page: ReviewPage,
  flags: ReviewFlag[],
  opened: Set<number>,
): StatusView {
  const s = chapterStatus(page, flags);
  const tone = STATUS[s.level as keyof typeof STATUS] ?? STATUS.unknown;
  const make = (
    level: StatusView['level'],
    word: string,
    icon: string = tone.icon,
    needsReview = false,
  ): StatusView => {
    const t = STATUS[level] ?? STATUS.unknown;
    return {level, word, icon, fg: t.fg, bg: t.bg, needsReview};
  };

  if (s.level === 'bad')
    return make(
      'bad',
      s.hard ? 'Failing' : 'Check',
      s.hard ? 'alert-circle' : 'alert-circle-outline',
      true,
    );
  if (s.level === 'warn') {
    const flagged = s.label === 'needs a look';
    return make(
      'warn',
      flagged ? 'Flagged' : 'Glance',
      flagged ? 'flag' : 'warning',
      true,
    );
  }
  if (isPuzzle(page) && !opened.has(page.id)) {
    return {
      ...make('unknown', 'Not opened', 'ellipse-outline'),
      needsReview: true,
    };
  }
  if (s.level === 'fixed') return make('fixed', 'Fixed', 'construct');
  if (s.level === 'dismissed') return make('dismissed', 'Dismissed', 'eye-off');
  if (s.level === 'unknown') return make('unknown', 'Unchecked', 'help-circle');
  return make(
    'good',
    page.content_type === 'video' ? 'Video' : 'Proven',
    page.content_type === 'video' ? 'videocam' : 'checkmark-circle',
  );
}

/** "12/27 reviewed": puzzles opened, which is what the publish gate counts. */
export function reviewProgress(
  pages: ReviewPage[],
  opened: Set<number>,
): {done: number; total: number} {
  const puzzles = pages.filter(isPuzzle);
  return {
    done: puzzles.filter(p => opened.has(p.id)).length,
    total: puzzles.length,
  };
}

export type RailRow = {
  chapterId: number;
  number: number;
  pageId: number;
  title: string;
  kind: 'puzzle' | 'video';
  status: StatusView;
};

/**
 * The rail, one row per chapter. The title is the chapter's own (neutral,
 * spoiler-safe) title; the chapter's first page is the one the editor opens.
 */
export function railRows(
  chapters: ReviewChapter[],
  flags: ReviewFlag[],
  opened: Set<number>,
  needsReviewOnly: boolean,
): RailRow[] {
  const rows: RailRow[] = [];
  for (const c of chapters) {
    const page = c.pages?.[0];
    if (!page) continue;
    const status = statusView(page, flags, opened);
    if (needsReviewOnly && !status.needsReview) continue;
    rows.push({
      chapterId: c.id,
      number: c.number,
      pageId: page.id,
      title: (c.title || page.heading || `Chapter ${c.number}`).trim(),
      kind: isPuzzle(page) ? 'puzzle' : 'video',
      status,
    });
  }
  return rows;
}

/** Where "Next to review" goes: the next flagged chapter, then an unopened puzzle. */
export function upNext(
  pages: ReviewPage[],
  flags: ReviewFlag[],
  currentId: number,
  opened: Set<number>,
): {id: number; why: 'flagged' | 'unopened'} | null {
  return nextToReview(pages, flags, currentId, opened);
}

// ---------------------------------------------------------------------------
// Placeholders for the next test groups (filled in test-first).

// ---------------------------------------------------------------------------
// The empty chat: what to suggest

const stillOpen = (f: ReviewFlag) =>
  f.status === 'open' ||
  (f.status === 'dismissed' && !(f.dismiss_reason || '').trim());

/** What each open flag kind asks the reviewer to do, as something to say. */
const FLAG_PROMPTS: Record<string, string> = {
  side: 'The side to move is wrong — flip it',
  solution: "Use the engine's line as the solution",
  hint: 'The hint gives the move away — rewrite it',
  title: 'Give this chapter a better title',
  position: 'The position looks wrong — what did the coach show?',
  boundary: 'Fix where this chapter starts and ends',
  not_a_puzzle: "This isn't a puzzle — keep it as video",
};

/**
 * Two or three things worth saying to the assistant about this chapter,
 * drawn from what is actually wrong with it: the machine checks first (they
 * block publishing), then the open flags, then what the pipeline doubted.
 * A clean chapter still gets useful openers rather than an empty box.
 */
export function suggestedPrompts(
  page: ReviewPage,
  flags: ReviewFlag[],
): string[] {
  const out: string[] = [];
  const add = (s: string) => {
    if (s && !out.includes(s)) out.push(s);
  };
  const mine = flags.filter(f => f.page_id === page.id && stillOpen(f));
  const review = page.config?.review;

  if (page.content_type === 'video') {
    for (const f of mine)
      add(FLAG_PROMPTS[f.kind] ?? 'Why is this chapter flagged?');
    add('Was a puzzle missed in this part?');
    add('Move the start to where the coach begins');
    add('Fix where this chapter starts and ends');
    return out.slice(0, 3);
  }

  if (review?.hard) {
    add('Why does this fail the machine check?');
    add("Use the engine's line as the solution");
  }
  for (const f of mine)
    add(FLAG_PROMPTS[f.kind] ?? 'Why is this chapter flagged?');
  const primary = review?.lines?.[0];
  const strip = (m: string) => m.replace(/[+#]/g, '');
  if (
    review?.coach_move &&
    (!primary || strip(primary.san[0] ?? '') !== strip(review.coach_move))
  ) {
    add(`Make the coach's move ${review.coach_move} the answer`);
  }
  if (review?.side_source === 'detector') add('Is the side to move right?');
  if (
    page.config?.title_source === 'template' &&
    !(review as any)?.title_accepted
  )
    add('Give this chapter a better title');
  if (review && (review.score === 'bad' || review.score === 'warn'))
    add('Why is this flagged?');
  if (out.length < 2) {
    add('Does the hint give the move away?');
    add('Explain the solution in one line');
  }
  return out.slice(0, 3);
}

// ---------------------------------------------------------------------------
// The one line under the board

export type SummaryLine = {
  side: string;
  goal: string;
  solution: string;
  machine: {
    level: 'good' | 'warn' | 'bad';
    word: string;
    icon: string;
    fg: string;
  };
};

const sideOf = (fen: string) =>
  (fen || '').split(' ')[1] === 'b' ? 'Black' : 'White';

/** side · goal · solution SAN · machine status — the puzzle at a glance. */
export function summaryLine(page: ReviewPage): SummaryLine {
  const review = page.config?.review;
  const task = (page.config as any)?.task as
    | {goal?: string | null; mate_in?: number | null}
    | undefined;
  const goalWord = (
    goal: string | null | undefined,
    mateIn?: number | null,
  ) => {
    if (goal === 'mate') return mateIn ? `Mate in ${mateIn}` : 'Mate';
    if (goal === 'draw') return 'Draw';
    if (goal === 'win') return 'Win';
    // Drills the coach launched in class (`pages.puzzle_task`).
    if (goal === 'check') return 'Give check';
    if (goal === 'escape') return 'Out of check';
    return '';
  };
  // A spoiler-safe page's task is what the student reads, so the reviewer
  // sees the same claim — never review.goal, which defaults to "win" with no
  // evidence. Only legacy pages (no task) fall back to it.
  const goal = task
    ? goalWord(task.goal, task.mate_in)
    : goalWord(review?.goal ?? 'win');
  const line = review?.lines?.[0]?.san ?? [];

  const machine = review?.machine ?? [];
  let m: SummaryLine['machine'];
  if (review?.hard || machine.some(s => s[0] === 'bad')) {
    m = {
      level: 'bad',
      word: 'Fails a machine check',
      icon: 'alert-circle',
      fg: STATUS.bad.fg,
    };
  } else if (machine.some(s => s[0] === 'warn')) {
    m = {
      level: 'warn',
      word: 'Machine warning',
      icon: 'warning',
      fg: STATUS.warn.fg,
    };
  } else {
    m = {
      level: 'good',
      word: 'Checks pass',
      icon: 'checkmark-circle',
      fg: STATUS.good.fg,
    };
  }
  return {
    side: `${sideOf(page.content)} to play`,
    goal,
    solution: line.length ? line.join(' ') : 'No solution yet',
    machine: m,
  };
}

// ---------------------------------------------------------------------------
// Publish gate: what fails, and where to go

export type FailingCheck = {
  label: string;
  detail: string;
  chapters: number[];
  jumpPageId: number | null;
};

/**
 * The gate's failing checks, each with the chapters it names and the first
 * one to jump to. The server words its details with page ids ("Not yet
 * opened: 12"), which a reviewer cannot act on; chapter numbers they can.
 */
export function failingChecks(
  gate: {ok: boolean; checks: {label: string; ok: boolean; detail: string}[]},
  chapters: ReviewChapter[],
): FailingCheck[] {
  const chapterOfPage = new Map<number, number>();
  for (const c of chapters)
    for (const p of c.pages ?? []) chapterOfPage.set(p.id, c.number);
  return (gate?.checks ?? [])
    .filter(c => !c.ok)
    .map(c => {
      const ids = (String(c.detail ?? '').match(/\d+/g) ?? [])
        .map(Number)
        .filter(n => chapterOfPage.has(n));
      const unique = Array.from(new Set(ids));
      return {
        label: c.label,
        detail: c.detail,
        chapters: unique.map(id => chapterOfPage.get(id)!),
        jumpPageId: unique.length ? unique[0] : null,
      };
    });
}

// ---------------------------------------------------------------------------
// Errors and keys

/**
 * A failed edit, undo or chat request, said calmly. The server's own words
 * when it gave a reason (they name the rule, e.g. the spoiler word); a plain
 * sentence otherwise. The input is never cleared on failure, so nothing here
 * has to apologise for lost text.
 */
export function chatErrorText(error: unknown): string {
  const response = (error as any)?.response;
  const server = response?.data?.error;
  if (!response) {
    // A chat turn may take a minute; the client waits 90s. Running out of that
    // is the assistant being slow, not the reviewer's connection.
    const timedOut =
      (error as any)?.code === 'ECONNABORTED' ||
      /timeout/i.test(String((error as any)?.message ?? ''));
    if (timedOut)
      return 'The assistant took too long to answer, so nothing was changed. Try again.';
    return "Couldn't reach the server. Your message is still here — send it again when you're connected.";
  }
  switch (response.status) {
    case 403:
      return "You don't have permission to edit this course.";
    case 400:
      return server || 'Messages can be up to 1000 characters.';
    case 404:
      return server || 'Nothing to undo here.';
    case 409:
      return (
        server || 'This changed after that edit. Undo the later change first.'
      );
    case 422:
      return server
        ? `Not applied: ${server}`
        : "That change couldn't be applied. Nothing was changed.";
    case 429:
      return 'That was a lot of messages in a minute. Wait a few seconds, then send again.';
    default:
      return (
        server ||
        'The server had a problem, so nothing was changed. Try again in a moment.'
      );
  }
}

type KeyLike = {
  key?: string;
  shiftKey?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
  isComposing?: boolean;
};

/** Enter sends; Shift+Enter is a newline; an IME composition is never cut short. */
export function shouldSendOnKey(e: KeyLike): boolean {
  return e?.key === 'Enter' && !e.shiftKey && !e.isComposing;
}

/** Cmd/Ctrl+Z undoes the latest edit — but never steals a text field's own undo. */
export function isUndoShortcut(
  e: KeyLike,
  active: {tagName?: string; isContentEditable?: boolean} | null | undefined,
): boolean {
  if (!e || (e.key !== 'z' && e.key !== 'Z')) return false;
  if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return false;
  const tag = (active?.tagName || '').toUpperCase();
  if (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    active?.isContentEditable
  )
    return false;
  return true;
}

// ---------------------------------------------------------------------------
// Play mode

/** A UCI line as the scripted moves the run reducer plays. */
const lineToMoves = (uci: string[]): CourseMove[] =>
  uci.map((move, i) => ({ply: i + 1, move}));

/**
 * The reviewer's play-through of a puzzle: the primary accepted line, with
 * every other accepted line as an alternative the reducer will follow.
 * Courses without review lines fall back to the page's own scripted moves.
 */
export function runForPage(page: ReviewPage): PuzzleRun {
  const lines = (page.config?.review?.lines ?? []).filter(
    l => Array.isArray(l?.uci) && l.uci.length,
  );
  const pageMoves = ((page as any).moves ?? null) as CourseMove[] | null;
  const primary = lines[0] ? lineToMoves(lines[0].uci) : pageMoves;
  return initialPuzzleRun(
    page.content,
    primary,
    lines.slice(1).map(l => l.uci),
  );
}

/** Every move on the board, in SAN — the wrong one last when one is on show. */
export function playedSan(run: PuzzleRun | null): string[] {
  if (!run) return [];
  const moves = run.history.map(h => h.san);
  return run.phase === 'wrong' && run.wrong ? [...moves, run.wrong.san] : moves;
}

export type ChatContext = {
  video_time: number | null;
  played_san: string[];
  board_fen: string | null;
};

/** What the assistant is told about the screen alongside a message. */
export function chatContext({
  run,
  videoTime,
}: {
  run: PuzzleRun | null;
  videoTime: number | null;
}): ChatContext {
  return {
    video_time:
      videoTime != null && Number.isFinite(videoTime) ? videoTime : null,
    played_san: playedSan(run),
    board_fen: run ? puzzleBoardView(run).position : null,
  };
}

// ---------------------------------------------------------------------------
// What changed (for the 1s highlight)

export type ChangedField =
  | 'title'
  | 'position'
  | 'solution'
  | 'hint'
  | 'summary'
  | 'times';

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The parts of a page an edit changed, so only those flash. */
export function changedFields(
  before: ReviewPage,
  after: ReviewPage,
): ChangedField[] {
  const out: ChangedField[] = [];
  const b = before.config ?? {};
  const a = after.config ?? {};
  if ((before.heading ?? '') !== (after.heading ?? '')) out.push('title');
  if (before.content !== after.content) out.push('position');
  if (
    !same(
      b.review?.lines?.map(l => l.san),
      a.review?.lines?.map(l => l.san),
    )
  )
    out.push('solution');
  if ((b.hint ?? '') !== (a.hint ?? '')) out.push('hint');
  if (
    (before.text ?? '') !== (after.text ?? '') ||
    !same(b.solution_summary, a.solution_summary)
  )
    out.push('summary');
  if (!same(b.video_segment, a.video_segment)) out.push('times');
  return out;
}

// ---------------------------------------------------------------------------
// Undo bookkeeping

/**
 * The server's revisions, newest first. Done once, on fetch: from then on the
 * list's ORDER is what says which batch is newest, because a batch recorded
 * client-side carries the browser's clock, which need not agree with the
 * server's.
 */
export function sortRevisions(revisions: Revision[]): Revision[] {
  return [...(revisions ?? [])].sort(
    (x, y) => Date.parse(y.created_at) - Date.parse(x.created_at),
  );
}

/** The one batch an undo would take back now: the newest not already undone. */
export function latestUndoableBatch(
  revisions: Revision[] | undefined | null,
): string | null {
  const live = (revisions ?? []).find(r => !r.undone_at);
  return live ? live.batch_id : null;
}

/**
 * Keep the cached history in step with a result, so the chips' undo state is
 * right without refetching: a new batch becomes the newest revision; an
 * undone one is marked, which makes the one before it undoable.
 */
export function applyResultToHistory(
  revisions: Revision[] | undefined | null,
  result: Pick<EditResult, 'batch'>,
  source: Revision['source'],
  now: string = new Date().toISOString(),
): Revision[] {
  const list = revisions ?? [];
  const batch = result?.batch;
  if (!batch?.id) return list;
  if (batch.undone) {
    return list.map(r =>
      r.batch_id === batch.id ? {...r, undone_at: r.undone_at ?? now} : r,
    );
  }
  if (list.some(r => r.batch_id === batch.id)) return list;
  return [
    {
      batch_id: batch.id,
      source,
      labels: batch.labels ?? [],
      created_by: null,
      created_at: now,
      undone_at: null,
    },
    ...list,
  ];
}
